import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Square, Maximize2, Minimize2 } from "lucide-react";
import Layout from "../components/Layout";
import VoiceToggle from "../components/VoiceToggle";
import VoiceHelp from "../components/VoiceHelp";
import ArrowSvg from "../components/ArrowSvg";
import {
  useSpeechRecognition,
  getSRLang,
} from "../hooks/useSpeechRecognition";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Reusable arrow trainer used by both the 4-direction and 8-direction
 * (with diagonals) screens.
 *
 * Timing:
 *  - length of time (arrow visible): user-controlled via slider, default 0.8 s
 *  - delay time (between arrows):    fixed 0.3 s
 */
const DEFAULT_LENGTH_MS = 800;
const DELAY_MS = 300;
const MIN_LENGTH_MS = 300;
const MAX_LENGTH_MS = 3000;

export default function ArrowTrainer({
  directions,
  titleKey,
  accent = "cyan",
  testid = "arrows-page",
}) {
  const { t, lang } = useLanguage();
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const [voice, setVoice] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [lengthMs, setLengthMs] = useState(DEFAULT_LENGTH_MS);
  const cycleRef = useRef(null);
  const hideRef = useRef(null);
  const stageRef = useRef(null);
  const runningRef = useRef(false);

  useEffect(() => {
    runningRef.current = running;
  }, [running]);

  const handleStart = useCallback(() => {
    if (runningRef.current) return;
    setRunning(true);
  }, []);
  const handleStop = useCallback(() => {
    if (!runningRef.current) return;
    setRunning(false);
  }, []);
  const handleToggle = useCallback(() => {
    if (runningRef.current) handleStop();
    else handleStart();
  }, [handleStart, handleStop]);

  const { supported: voiceSupported, listening } = useSpeechRecognition({
    enabled: voice,
    lang: getSRLang(lang),
    onStart: handleStart,
    onStop: handleStop,
  });

  useEffect(() => {
    if (!running) {
      if (cycleRef.current) clearInterval(cycleRef.current);
      if (hideRef.current) clearTimeout(hideRef.current);
      cycleRef.current = null;
      hideRef.current = null;
      setVisible(false);
      return undefined;
    }
    setCount(0);
    const cycleMs = lengthMs + DELAY_MS;
    let prev = null;
    const pick = () => {
      let next = directions[Math.floor(Math.random() * directions.length)];
      if (prev && directions.length > 1) {
        while (next === prev) {
          next = directions[Math.floor(Math.random() * directions.length)];
        }
      }
      prev = next;
      return next;
    };
    const showOne = () => {
      setCurrent(pick());
      setVisible(true);
      setCount((c) => c + 1);
      if (hideRef.current) clearTimeout(hideRef.current);
      hideRef.current = setTimeout(() => setVisible(false), lengthMs);
    };
    showOne();
    cycleRef.current = setInterval(showOne, cycleMs);
    return () => {
      if (cycleRef.current) clearInterval(cycleRef.current);
      if (hideRef.current) clearTimeout(hideRef.current);
    };
  }, [running, directions, lengthMs]);

  const toggleFs = async () => {
    const el = stageRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setIsFs(true);
      } else {
        await document.exitFullscreen();
        setIsFs(false);
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const onChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return (
    <Layout showBack>
      <div className="vls-page vls-page-trainer" data-testid={testid}>
        {!running && (
          <h1 className="vls-page-title">
            <span className={`vls-grad-${accent}`}>{t(titleKey)}</span>
          </h1>
        )}

        <div
          ref={stageRef}
          className="vls-color-stage vls-stage-fit vls-stage-arrows"
          data-testid="arrow-stage"
        >
          <div className="vls-arrow-box">
            {running && current && visible ? (
              <ArrowSvg direction={current} />
            ) : !running ? (
              <div className="vls-stage-placeholder">
                <p>{t("tapToStart")}</p>
                {voiceSupported && (
                  <p className="vls-stage-hint">{t("voiceHint")}</p>
                )}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="vls-fs-btn"
            onClick={toggleFs}
            aria-label={isFs ? t("exitFullscreen") : t("fullscreen")}
            data-testid="fullscreen-button"
          >
            {isFs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>

        <div className="vls-stats">
          <div className="vls-stat">
            <span className="vls-stat-label">{t("tasksLabel")}</span>
            <span className="vls-stat-value" data-testid="stat-count">
              {count}
            </span>
          </div>
        </div>

        {!running && (
          <div className="vls-interval-row">
            <label className="vls-interval-label" htmlFor="interval-slider">
              {t("interval")}:{" "}
              <strong data-testid="interval-value">
                {(lengthMs / 1000).toFixed(1)}s
              </strong>
            </label>
            <input
              id="interval-slider"
              type="range"
              min={MIN_LENGTH_MS}
              max={MAX_LENGTH_MS}
              step="100"
              value={lengthMs}
              onChange={(e) => setLengthMs(Number(e.target.value))}
              data-testid="interval-slider"
            />
          </div>
        )}

        <div className="vls-controls">
          <button
            type="button"
            className={`vls-btn-primary ${running ? "stop" : ""}`}
            onClick={handleToggle}
            data-testid="start-stop-button"
          >
            {running ? <Square size={22} /> : <Play size={22} />}
            <span>{running ? t("stop") : t("start")}</span>
          </button>
          <VoiceToggle
            enabled={voice}
            setEnabled={setVoice}
            supported={voiceSupported}
            listening={listening}
          />
        </div>

        {!running && <VoiceHelp supported={voiceSupported} />}
      </div>
    </Layout>
  );
}
